import type { State } from 'src/replicant/State';
import StateObserver from 'src/StateObserver';
import ab from 'src/replicant/ruleset/ab';
import { isDynamicTestInBucket } from 'src/replicant/getters/ab';
import { escalatingContinueRetryBonuses } from 'src/replicant/match2/match2.ruleset';

/**
 * Calculate bonus moves for the first gem continue after previous losses.
 *
 * @param playerState Current player eligibility and loss streak.
 * @param continues Number of continues used in the current attempt.
 * @returns Bonus moves to add to the regular continue offer, or zero when ineligible.
 */
export function getExtraMovesOffer(
  playerState: State,
  continues: number,
): number {
  // Only players in the enabled test bucket receive bonus moves.
  const enabled = isDynamicTestInBucket(
    playerState,
    ab.tests.TEST_ESCALATING_EXTRA_MOVES,
    'enabled',
  );
  if (!enabled) return 0;

  // Only the first continue in an attempt receives the bonus.
  if (continues > 0) return 0;

  // The first attempt has no prior losses to reward.
  const priorLosses = playerState.match2.losingStreak;
  if (priorLosses < 1) return 0;

  // Players with any purchase history are ineligible.
  const numPurchases = StateObserver.replicant.getPurchaseHistory().length;
  if (numPurchases > 0) return 0;

  // Cap the loss tier at the final configured bonus.
  const tier = Math.min(priorLosses, escalatingContinueRetryBonuses.length - 1);

  // Return the bonus added to both the displayed offer and granted moves.
  return escalatingContinueRetryBonuses[tier];
}
